import { useEffect, useRef, useState } from 'react'

/*
  A settings value that reads as a value, not as a form field.

  A screen of filled-in inputs asks the owner to re-read their own answers every visit and
  makes a company profile feel like paperwork. Showing the settled value and revealing the
  edit control on hover puts the emphasis where it belongs: this is what the board knows
  about you. Editing is the exception, so it costs a click.

  The chevron is aria-hidden and the whole row is a button, so keyboard and screen reader
  users get one target with a real name instead of an icon.

  ── It used to be a pencil, revealed on hover ───────────────────────────────

  Which meant that on every phone, where there is no hover, a row of settled values looked
  like static text and nothing on the screen said any of it could be changed. The same
  mistake as the roster's "Ask" button, and fixed the same way: the affordance stands at
  rest. A chevron rather than a pencil, because the row opens something.
*/

export type RowKind = 'text' | 'number' | 'money' | 'url' | 'date' | 'textarea' | 'select'

export interface SelectOption {
  value: number
  label: string
}

interface Props {
  label: string
  value: string | number | null
  kind?: RowKind
  hint?: string
  placeholder?: string
  options?: SelectOption[]
  /**
   * Put on the row's own button so something else on the screen can point at it. The
   * company profile's "what the board reads" panel presses these: the blanks in that
   * paragraph are the unfilled rows, and pressing one has to open the row it names.
   */
  id?: string
  /**
   * Draw a mark in the gutter when there is no value yet. Off by default, because on
   * Settings an unset row is a choice not a gap. On the company profile every unset row
   * is something the board is guessing at, and four identical greys reading "Not set"
   * meant the owner had to read every row to find the work.
   */
  markEmpty?: boolean
  onSave: (value: string | number | null) => Promise<void>
}

export function SettingRow({
  label, value, kind = 'text', hint, placeholder, options, id, markEmpty, onSave,
}: Props) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const field = useRef<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(null)

  useEffect(() => {
    if (!editing) return
    field.current?.focus()
    if (field.current instanceof HTMLInputElement) field.current.select()
  }, [editing])

  const open = () => {
    setDraft(value === null || value === undefined ? '' : String(value))
    setEditing(true)
  }

  const commit = async () => {
    if (saving) return
    setSaving(true)
    try {
      await onSave(parse(draft, kind))
      setEditing(false)
    } finally {
      setSaving(false)
    }
  }

  const empty = value === null || value === undefined || value === ''

  if (!editing) {
    return (
      <button
        id={id}
        className={`row${empty && markEmpty ? ' row--gap' : ''}`}
        onClick={open}
        aria-label={`Edit ${label.toLowerCase()}`}
      >
        <span className="row__label">{label}</span>
        <span className={`row__value ${empty ? 'row__value--empty' : ''}`}>
          {display(value, kind, options) || 'Not set'}
        </span>
        <span className="row__edit" aria-hidden>{CHEVRON}</span>
      </button>
    )
  }

  return (
    <div className="row row--editing">
      <span className="row__label">{label}</span>

      <div className="row__field">
        {kind === 'textarea' ? (
          <textarea
            ref={field as React.RefObject<HTMLTextAreaElement>}
            className="textarea"
            value={draft}
            placeholder={placeholder}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Escape') setEditing(false) }}
          />
        ) : kind === 'select' ? (
          <select
            ref={field as React.RefObject<HTMLSelectElement>}
            className="input"
            value={draft}
            onChange={e => setDraft(e.target.value)}
          >
            {options?.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        ) : (
          <input
            ref={field as React.RefObject<HTMLInputElement>}
            className="input"
            type={inputType(kind)}
            step={kind === 'money' ? 1000 : undefined}
            min={kind === 'money' || kind === 'number' ? 0 : undefined}
            value={draft}
            placeholder={placeholder}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => {
              // Enter commits, Escape abandons. A multi-line summary keeps Enter for what
              // Enter is for and commits on the button instead.
              if (e.key === 'Enter') { e.preventDefault(); void commit() }
              if (e.key === 'Escape') setEditing(false)
            }}
          />
        )}

        {hint && <span className="dim">{hint}</span>}

        <div className="row__actions">
          <button className="btn btn--primary" disabled={saving} onClick={commit}>
            {saving ? 'Saving' : 'Done'}
          </button>
          <button className="btn btn--quiet" onClick={() => setEditing(false)}>Cancel</button>
        </div>
      </div>
    </div>
  )
}

function inputType(kind: RowKind): string {
  if (kind === 'money' || kind === 'number') return 'number'
  if (kind === 'url') return 'url'
  if (kind === 'date') return 'date'
  return 'text'
}

function parse(draft: string, kind: RowKind): string | number | null {
  const trimmed = draft.trim()
  if (trimmed === '') return null

  if (kind === 'money' || kind === 'number' || kind === 'select') {
    const n = Number(trimmed)
    return Number.isFinite(n) ? n : null
  }

  return trimmed
}

function display(
  value: string | number | null, kind: RowKind, options?: SelectOption[],
): string {
  if (value === null || value === undefined || value === '') return ''

  if (kind === 'select') {
    return options?.find(o => o.value === Number(value))?.label ?? String(value)
  }

  // Whole dollars with separators. An owner reading "2400000" has to count digits, which
  // is exactly the friction this screen exists to remove.
  if (kind === 'money') {
    return Number(value).toLocaleString(undefined, {
      style: 'currency', currency: 'USD', maximumFractionDigits: 0,
    })
  }

  if (kind === 'date') {
    const d = new Date(String(value))
    return Number.isNaN(d.getTime())
      ? String(value)
      : d.toLocaleDateString(undefined, { day: 'numeric', month: 'long' })
  }

  return String(value)
}

/* The same mark the Company hub uses on its rows, at the same weight. One row shape. */
const CHEVRON = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="m9 5 7 7-7 7" />
  </svg>
)
